import { useEffect, useState } from 'react';
import api from '../services/api';
import useAuth from './useAuth';

export interface UseFetchResult<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

export function useFetch<T>(url: string, params?: Record<string, any>, enabled: boolean = true, timeoutMs?: number): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setLoading(true);
    setError(null);

    if (!enabled) {
      setLoading(false);
      return;
    }

    if (!user?.access_token) {
      setLoading(false);
      return;
    }

    api.get<T>(url, {
      params,
      signal,
      ...(timeoutMs !== undefined && { timeout: timeoutMs }),
    }).then(response => {
      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }
      setError(null);
      setData(response.data);
      setLoading(false)
    }).catch((error) => {
      if (error.name !== 'CanceledError') {
        setError((error as Error).message);
        setLoading(false)
      }
    });

    return () => {
      controller.abort();
    };
  }, [user?.access_token, params, enabled, timeoutMs]);

  return { data, error, loading };
}
